import React, { useMemo, useState, useEffect } from 'react';
import { BubbleOverlay, BubbleTable, BubbleButton, BubbleBadge } from './BubbleUI';
import { BareSailingDay, BareSailing } from '@go-shared/types/sailing';
import { TemplateFragmentType } from '@go-shared/types/templateFragment';
import { Template } from '@go-shared/types/template';
import { BubbleTableFields, BubbleTableTemplate } from './BubbleUI/BubbleTable';
import { camelToSentenceCase } from '@go-shared/lib/string';
import { getLocaleUTCDateString } from '@go-shared/lib/time';
import { safeIdCompare } from '@go-shared/lib/utils';
import { getSailingsByTemplateFragment } from '@go-client/lib/api/itinerary';
import { BubbleBadgePropsBase } from './BubbleUI/BubbleBadge';

interface TemplateViewFormTableItem {
  key: string;
  name: string;
  voyage: string;
  date: string;
  fragments: TemplateFragmentType[];
  _sailing: BareSailing;
  _sailingDay: BareSailingDay;
}

interface TemplateAssociationPropsBase {
  template: Template;
  onSailingDayNameClick?: (sailing: BareSailing, sailingDay: BareSailingDay) => void;
}

type TemplateAssociationProps = React.HTMLAttributes<HTMLDivElement> & TemplateAssociationPropsBase;

export default function TemplateAssociations(props: TemplateAssociationProps) {
  const { template, onSailingDayNameClick, ...rest } = props;

  const [loading, setLoading] = useState(false);
  const [sailings, setSailings] = useState<BareSailing[]>([]);

  useEffect(() => {
    fetchAssociatedSailings().catch(console.error);
  }, [props.template]);

  async function fetchAssociatedSailings() {
    setLoading(true);
    try {
      const sails = await getSailingsByTemplateFragment(props.template._id);
      setSailings(sails);
    } finally {
      setLoading(false);
    }
  }

  const tableItems: TemplateViewFormTableItem[] = useMemo(() => {
    const items: TemplateViewFormTableItem[] = [];

    sailings.forEach(sailing => {
      sailing.days.forEach(day => {
        const { templateFragments } = day;
        const fragments: TemplateFragmentType[] = [];

        if (
          templateFragments &&
          templateFragments.events &&
          safeIdCompare(templateFragments.events, template._id)
        ) {
          fragments.push('events');
        }

        if (
          templateFragments &&
          templateFragments.venueData &&
          safeIdCompare(templateFragments.venueData, template._id)
        ) {
          fragments.push('venueData');
        }

        if (
          templateFragments &&
          templateFragments.feed &&
          safeIdCompare(templateFragments.feed, template._id)
        ) {
          fragments.push('feed');
        }

        if (fragments.length < 1) return;

        items.push({
          key: day._id,
          voyage: day.voyage,
          name: `Day ${day.name}`,
          date: getLocaleUTCDateString(day.date),
          fragments,
          _sailing: sailing,
          _sailingDay: day,
        });
      });
    });

    return items;
  }, [sailings]);

  const tableFields: Array<BubbleTableFields<TemplateViewFormTableItem>> = [
    {
      key: 'voyage',
    },
    {
      key: 'name',
      thProps: { className: 'pl-4-i' },
      tdProps: { className: 'relative p-0-i' },
    },
    {
      key: 'date',
    },
    {
      key: 'fragments',
      thProps: {
        className: 'pl-1-i',
      },
      tdProps: { className: 'p-1-i' },
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<TemplateViewFormTableItem>> = [
    {
      key: 'name',
      render: item => (
        <BubbleButton
          variant="navy"
          inverse
          className="stretched-link text-left"
          icon={['fass', 'chart-tree-map']}
          onClick={() => {
            if (props.onSailingDayNameClick) {
              props.onSailingDayNameClick(item._sailing, item._sailingDay);
            }
          }}
        >
          <span className="underline">{item.name}</span>
        </BubbleButton>
      ),
    },
    {
      key: 'fragments',
      render: item => (
        <div className="-mr-1 -mb-1">
          {item.fragments.map(frg => {
            const variantDict: { [key in TemplateFragmentType]: BubbleBadgePropsBase['variant'] } =
              {
                events: 'lime',
                venueData: 'banana',
                content: 'ocean',
                feed: 'grape',
              };

            return (
              <BubbleBadge key={frg} className="mr-1 mb-1" variant={variantDict[frg]}>
                {camelToSentenceCase(frg)}
              </BubbleBadge>
            );
          })}
        </div>
      ),
    },
  ];

  return (
    <div {...rest}>
      <BubbleOverlay loading={loading}>
        <BubbleTable
          responsive
          headVariant="navy"
          striped
          fields={tableFields}
          items={tableItems}
          templates={tableTemplates}
        />
      </BubbleOverlay>
    </div>
  );
}
